import type { ReactElement, ReactNode } from 'react';
import type {
  ModalBodyProps,
  ModalContentProps,
  ModalProps,
  SystemStyleObject,
} from '@chakra-ui/react';
import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';

import type { Optional } from '@izimi/utils';

import { useModalStore } from './modalStore';

interface Props extends Omit<Optional<ModalProps, 'onClose' | 'isOpen'>, 'children'> {
  headerLeft?: ReactNode;
  title?: string;
  RenderBody: ReactNode;
  RenderFooter?: ReactElement;
  modalBodyProps?: ModalBodyProps;
  showCloseButton?: boolean;
  fullHeight?: boolean;
  modalContentProps?: ModalContentProps;
}

const headerStyles: SystemStyleObject = {
  paddingInlineStart: '40px',
};

const BaseModal = ({
  title,
  showCloseButton = true,
  RenderBody,
  RenderFooter,
  modalBodyProps,
  headerLeft,
  isOpen,
  fullHeight,
  modalContentProps,
  onClose,
  ...modalProps
}: Props) => {
  const { hideModal } = useModalStore();

  return (
    <Modal isOpen={isOpen != null ? isOpen : true} onClose={onClose ?? hideModal} {...modalProps}>
      <ModalOverlay />
      <ModalContent
        containerProps={fullHeight ? { alignItems: 'stretch' } : undefined}
        {...modalContentProps}>
        {title && (
          <ModalHeader sx={headerStyles} position='relative'>
            {title}
            {headerLeft && (
              <Flex
                justifyContent='center'
                alignItems='center'
                position='absolute'
                left={0}
                top={0}
                height='100%'
                width='40px'>
                {headerLeft}
              </Flex>
            )}
          </ModalHeader>
        )}
        {showCloseButton && <ModalCloseButton onClick={onClose ?? hideModal} />}
        <ModalBody {...modalBodyProps}>{RenderBody}</ModalBody>
        {Boolean(RenderFooter) && <ModalFooter>{RenderFooter}</ModalFooter>}
      </ModalContent>
    </Modal>
  );
};

export default BaseModal;
